import { useState, useEffect, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const SLIDES = [
  {
    src: "/assets/cover-social-media.jpg",
    alt: "The Ultimate Guide to Mastering Social Media ebook — faceless digital marketing playbook to monetize social media and make money online",
  },
  {
    src: "/assets/cover-digital-marketing.jpg",
    alt: "Faceless Digital Marketing ebook with Master Resell Rights (MRR) — step-by-step blueprint to launch a faceless digital product business and earn passive income",
  },
  {
    src: "/assets/cover-etsy-pinterest.jpg",
    alt: "Mastering Etsy & Pinterest for Digital Success ebook with Master Resell Rights — sell digital products and grow a faceless creative side hustle",
  },
  {
    src: "/assets/cover-ai-design.jpg",
    alt: "The Ultimate AI & Design Accelerator ebook — use ChatGPT and Canva to create viral faceless content and automate social media growth",
  },
  {
    src: "/assets/cover-reels.jpg",
    alt: "The Ultimate Reels Accelerator Blueprint ebook — go viral with faceless Instagram Reels for massive followers and passive income on autopilot",
  },
];

const AUTOPLAY_MS = 4000;

export default function ProductSlideshow() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  const go = useCallback((next) => {
    setIndex((prev) => (next + SLIDES.length) % SLIDES.length);
  }, []);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => go(index + 1), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [index, paused, go]);

  return (
    <div
      className="relative"
      data-testid="product-slideshow"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="absolute -inset-6 bg-[#D4AF37]/10 blur-3xl rounded-full" />

      <div className="relative z-10 mx-auto w-full max-w-md">
        {/* Slide frame */}
        <div className="relative aspect-[3/4] overflow-hidden border border-zinc-800 bg-[#0C0C0C] drop-shadow-[0_20px_60px_rgba(0,0,0,0.8)]">
          <AnimatePresence mode="wait">
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 1.03 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="absolute inset-0"
            >
              {/* Blurred fill so every cover fills the frame regardless of its size */}
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-50"
                style={{ backgroundImage: `url(${SLIDES[index].src})` }}
              />
              <img
                src={SLIDES[index].src}
                alt={SLIDES[index].alt}
                data-testid={`slide-image-${index}`}
                className="relative z-10 w-full h-full object-contain"
                loading="lazy"
              />
            </motion.div>
          </AnimatePresence>

          {/* Arrows */}
          <button
            aria-label="Previous cover"
            data-testid="slide-prev"
            onClick={() => go(index - 1)}
            className="absolute z-20 left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/70 border border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            aria-label="Next cover"
            data-testid="slide-next"
            onClick={() => go(index + 1)}
            className="absolute z-20 right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-black/70 border border-zinc-700 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Dots */}
        <div className="mt-6 flex items-center justify-center gap-3">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`slide-dot-${i}`}
              onClick={() => setIndex(i)}
              className={`h-1.5 transition-all duration-300 ${
                i === index ? "w-8 bg-[#D4AF37]" : "w-3 bg-zinc-700 hover:bg-zinc-500"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
